import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, RedirectCommand, Router, RouterStateSnapshot, Routes } from '@angular/router';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { finalize, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { isLeasingCompanyUserGuard } from '@shared/guards/leasing-company/leasing-company.guard';
import { AdministrationService } from '@shared/services/leasing-company/administration/administration.service';
import { RsLoaderService } from '@lib/rs-ngx';
import { VehicleTypeService } from '@app/leasing-company/shared/services/vehicle-type.service';
import { ContractSetupDataset } from '@app/leasing-company/leasing-contracts/shared/models/contract-setup-dataset';
import { LeasingCompanyContract } from '@app/leasing-company/leasing-contracts/shared/models/leasing-company-contract';
import { LeasingContractService } from '@app/leasing-company/leasing-contracts/shared/services/leasing-contract.service';

function resolveContractSetupDataset(): Observable<RedirectCommand | ContractSetupDataset> {
  const administrationService = inject(AdministrationService);
  const vehicleTypeService = inject(VehicleTypeService);
  const rsLoaderService = inject(RsLoaderService);

  const spinnerId = 'resolveContractSetupDataset';
  rsLoaderService.show({ msg: 'LOADING.LOADING_DATAS', id: spinnerId });

  return administrationService.getLeasingCompanyAdministrationData().pipe(
    switchMap((adminData) => forkJoin({
      vehicleTypes: vehicleTypeService.getVehicleTypes(),
      allowBothContractTypes: of(
        adminData.parameters.allowCreationLeasingContracts &&
        adminData.parameters.allowCreationServiceContracts,
      ),
    })),
    finalize(() => rsLoaderService.hide({ id: spinnerId })),
  );
}

function canCreateLeasingContractGuard(): CanActivateFn {
  return (_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean | RedirectCommand> => {
    const administrationService = inject(AdministrationService);
    const router = inject(Router);

    return administrationService.getLeasingCompanyAdministrationData().pipe(
      map(adminData => {
        if (!adminData.parameters.allowCreationLeasingContracts && !adminData.parameters.allowCreationServiceContracts) {
          return new RedirectCommand(router.createUrlTree(['leasing-company', 'leasing-contracts']));
        }
        return true;
      }),
    );
  };
}

function resolveLeasingContract(activatedRouteSnapshot: ActivatedRouteSnapshot): Observable<LeasingCompanyContract> {
  const rsLoaderService = inject(RsLoaderService);
  const leasingContractService = inject(LeasingContractService);

  const spinnerId = 'resolveLeasingContract';
  rsLoaderService.show({ msg: 'LOADING.LOADING_DATAS', id: spinnerId });

  return leasingContractService
    .getLeasingContractById(Number(activatedRouteSnapshot.paramMap.get('id')!))
    .pipe(finalize(() => rsLoaderService.hide({ id: spinnerId })));
}

export const leasingContractsRoutes: Routes = [
  {
    path: '',
    providers: [LeasingContractService],
    children: [
      {
        path: '',
        loadComponent: () => import('@app/leasing-company/leasing-contracts/overview/overview.component').then(component => component.OverviewComponent),
        providers: [{ provide: STEPPER_GLOBAL_OPTIONS, useValue: { displayDefaultIndicatorType: false } }],
      },
      {
        path: 'add',
        loadComponent: () => import('@app/leasing-company/leasing-contracts/add/add.component'),
        canActivate: [
          canCreateLeasingContractGuard(),
          isLeasingCompanyUserGuard({
            roles: ['LEASING_COMPANY_MANAGER', 'LEASING_COMPANY_WITH_AUTHORIZATION'],
            urlToRedirect: '/leasing-company/leasing-contracts',
          }),
        ],
        resolve: { contractSetupDataset: resolveContractSetupDataset },
      },
      {
        path: ':id',
        loadComponent: () => import('@app/leasing-company/leasing-contracts/details/details.component').then(component => component.DetailsComponent),
        children: [
          {
            path: 'general',
            loadComponent: () => import('@app/leasing-company/leasing-contracts/details/general/general.component').then(component => component.GeneralComponent),
          },
          {
            path: 'activities',
            loadComponent: () => import('@app/leasing-company/leasing-contracts/details/activities/activities.component').then(component => component.ActivitiesComponent),
          },
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'general',
          },
        ],
      },
      {
        path: ':id/edit',
        loadComponent: () => import('@app/leasing-company/leasing-contracts/edit/edit.component').then(component => component.EditComponent),
        resolve: {
          leasingContract: resolveLeasingContract,
          contractSetupDataset: resolveContractSetupDataset,
        },
      },
      {
        path: '**',
        redirectTo: '',
      },
    ],
  },
];

